

import { Close as X } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';

import {
    EntityVisual,
    SectionCharacterMain,
    SectionDisplay,
    SectionLocationMain,
    SectionOrganizationMain
} from '@/components/entity-display';
import { useEntitySettings } from '@/features/shared/hooks/use-entity-settings';
import type { Character, Location, Organization } from '@/lib/types';
import { EnrichedEntity, PrimaryEntityType } from '@/lib/types';
import { resolvePrimaryImage } from '@/lib/utils/image-identity';
import { useTranslation } from 'react-i18next';


interface EntityDetailDialogProps {
  entity: EnrichedEntity | null;
  onClose: () => void;
}

export function EntityDetailContent({ entity, layout = 'dialog' }: { entity: EnrichedEntity | null; layout?: 'dialog' | 'drawer' }) {
  const { t } = useTranslation();
  const entityType = entity?.entityType;
  const settingsType = (entityType === 'characters' || entityType === 'locations' || entityType === 'organizations')
    ? entityType as PrimaryEntityType
    : 'characters';
  const { sections } = useEntitySettings(settingsType);
  const isRichEntity = entityType === 'characters' || entityType === 'locations' || entityType === 'organizations';

  if (!isRichEntity || !entity) {
    return <Box sx={{ p: 3 }}>
      {(entity as { description?: string })?.description && <Box sx={{ mb: 2 }}>{(entity as { description?: string }).description}</Box>}
      {(entity as { shortDescription?: string })?.shortDescription && <Box>{(entity as { shortDescription?: string }).shortDescription}</Box>}
    </Box>;
  }

  const details = <>
    {entityType === 'characters' && <SectionCharacterMain data={entity as Partial<Character>} />}
    {entityType === 'locations' && <SectionLocationMain data={entity as Partial<Location>} />}
    {entityType === 'organizations' && <SectionOrganizationMain data={entity as Partial<Organization>} />}
    {sections.map((section) => <SectionDisplay key={section.id} title={section.title} fields={section.fields} data={entity as Record<string, unknown>} />)}
  </>;
  const primaryImage = resolvePrimaryImage(entity);
  const images = entity.images && entity.images.length > 0 ? entity.images : (primaryImage ? [primaryImage] : []);
  const hasPrimaryImage = !!(primaryImage?.profile?.url || primaryImage?.original?.url);

  if (layout === 'drawer') {
    return <Box>
      {hasPrimaryImage && <Box sx={{ p: 2, maxWidth: 320, '& > .MuiPaper-root': { p: 0 }, '& > .MuiPaper-root > .MuiTypography-root': { display: 'none' } }}>
        <EntityVisual disableLayout title={t('editor.main_image')} altText={entity.name || ''} images={images} primaryImageId={entity.primaryImageId || primaryImage?.id} />
      </Box>}
      <Box sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        '& > .MuiPaper-root': { bgcolor: 'transparent', boxShadow: 'none', border: 0 },
      }}>{details}</Box>
    </Box>;
  }

  return <Box sx={{ p: { xs: 2, md: 4 } }}><Grid container spacing={4}>
    <EntityVisual title={t('editor.main_image')} altText={entity.name || ''} images={images} primaryImageId={entity.primaryImageId || primaryImage?.id} />
    <Grid size={{ xs: 12, lg: 8 }}><Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {details}
    </Box></Grid>
  </Grid></Box>;
}


export function EntityDetailDialog({ entity, onClose }: EntityDetailDialogProps) {
  const { t } = useTranslation();
  const entityType = entity?.entityType;

  const isRichEntity = entityType === 'characters' ||
    entityType === 'locations' ||
    entityType === 'organizations';

  return (
    <Dialog
      open={!!entity}
      onClose={onClose}
      maxWidth={isRichEntity ? "lg" : "md"}
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {entity?.name || entity?.title || t('editor.details')}
        <IconButton onClick={onClose} size="small">
          <X sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <EntityDetailContent entity={entity} />
      </DialogContent>

    </Dialog>
  );
}
